import S from 's-js';
import { IAjax } from 'lib/Ajax';
import { AssertExtends } from 'lib/assertExtends';
import { jsonizeSignals } from 'lib/jsonizeSignals';
import { RequestQueue } from 'lib/requestQueue';
import TZDate from 'lib/TZDate';

import { AddPortCallRequest } from 'data/models/AddPortCallRequest';
import { CoordinationCard } from 'data/models/CoordinationCard';
import { Domain } from 'data/models/Domain';
import { PortCall } from 'data/models/PortCall';
import { PreviewBatchDatesRequestData } from 'data/models/PreviewBatchDatesRequest';
import { PreviewBatchDatesResponseData } from 'data/models/PreviewBatchDatesResponse';
import { RecurringBatchRequest } from 'data/models/RecurringBatchRequest';
import { UserSummary } from 'data/models/UserSummary';

import { PCCache } from 'data/PCCache';

import { Entrypoints } from 'core/app/entrypoints';

import { PortCallScheduleHeader } from 'portcall/portcallschedule/header';

import {
    MetroMapFramework,
    MetroMapFrameworkParams,
    MetroMapMilestone,
    MetroMapMilestoneParams,
    MilestoneParams
} from 'portcall/metromapframework/model';

import { AddPortCallTabModel } from './model';

export interface AddPortCallReviewTabParams {
    PortCall : PortCall;
    Request : AddPortCallRequest;
    ScheduleHeader : PortCallScheduleHeader;
    OnReviewTab : () => boolean;
}

export type AddPortCallReviewTab = AssertExtends<ReturnType<typeof AddPortCallReviewTab>, AddPortCallTabModel>;
export function AddPortCallReviewTab(
    ajax : IAjax,
    entrypoints : Entrypoints,
    data: PCCache,
    domain : Domain,
    user : () => UserSummary | null,
    metroMapFrameworkModel : <TMilestone>(params : MetroMapFrameworkParams<TMilestone>) => MetroMapFramework<TMilestone>,
    metroMapMilestoneModel : (params : MetroMapMilestoneParams) => MetroMapMilestone,
    {
        PortCall,
        Request,
        ScheduleHeader,
        OnReviewTab
    } : AddPortCallReviewTabParams
) {
    const
        ccardModel = CoordinationCardViewModel,
        milestoneModel = (params : MilestoneParams) => ReviewTabMilestone(
            metroMapMilestoneModel,
            ccardModel,
            params
        ),
        Framework = metroMapFrameworkModel({
            PortCall,
            MilestoneConstructor: milestoneModel
        }),
        ShowAgentAffirmation = S.value(false),
        AgentAffirmation = S(() => ShowAgentAffirmation() ?
            () => ShowAgentAffirmation(false) :
            null
        ),
        requiresAgentAffirmation = S.value(false),
        hasAccepted = S.value(false);
    
    // DC 07/14/25 -- We need a way to spawn a "Agree to Terms & Conditions" popup for Hawaii.
    // This isn't ideal, but it gets the job done by looking at each Port under one of the DOT
    // District's control. There are two options (2nd commented out): standard and agents only.
    S(() => {
        if (OnReviewTab()) {
            // The following are the PortIds of DOT ports.
            // 11 = Honolulu - Oahu (Oahu District)
            // 13 = Hilo - Hawai‘i (Hawaii District)
            // 14 = Kalaeloa Barbers Point = Oahu (Oahu District)
            // 15 = Kahului - Maui (Maui District)
            // 16 = Nawiliwili - Kauai (Kauai District)
            // 17 = Port Allen - Kauai (Kauai District)
            // 18 = Kaumalapau - Lanai (Maui District)
            // 19 = Kaunakakai - Moloka‘i (Maui District)
            // 20 = Kawaihae - Hawai‘i (Hawaii District)
            if (PortCall.PortId() == 11 || 
                PortCall.PortId() == 13 || 
                PortCall.PortId() == 14 || 
                PortCall.PortId() == 15 || 
                PortCall.PortId() == 16 || 
                PortCall.PortId() == 17 || 
                PortCall.PortId() == 18 || 
                PortCall.PortId() == 19 || 
                PortCall.PortId() == 20) {
                // Option 1: Sans-permissions
                requiresAgentAffirmation(true);
                ShowAgentAffirmation(true);

                // // Option 2: Con-permissions, i.e. show for only Agents
                // const userId = user()?.Id();
                // var isAgent = false;
                // var isAdmin = false;
                // if (userId != null) {
                //     // Note: brittle; order of operations could mess it up
                //     // Would be better to have an array that we add to and check (or something else)
                //     data.Users.index(userId!)()?.Roles().forEach((role) => {
                //         // console.log(role.Role()?.Id()); // Would be "safer"
                //         // console.log(role.Role()?.Name());
                //         if (role.Role()?.Name()?.match('Reserve Berths')) {
                //             isAgent = true;
                //         }
                //         if (role.Role()?.Name()?.match('Manage Berths')) {
                //             isAdmin = true;
                //         }
                //         if (role.Role()?.Name()?.match('Site Admin')) {
                //             isAdmin = true;
                //         }
                //     });
                //     if (isAgent && !isAdmin) {
                //         console.log("is only an agent, show agreement");
                //         requiresAgentAffirmation(true);
                //         ShowAgentAffirmation(true);
                //     }
                // }
            }
        }
    });

    const BatchRequest = S(() => Request.BatchRequest() && BatchRequestViewModel(
        ajax,
        entrypoints,
        domain,
        Request.BatchRequest()!,
        PortCall,
        OnReviewTab,
    ));

    const UpdatingPreview = () =>
        !!BatchRequest()?.UpdatingPreview();

    return {
        ScheduleHeader,
        Framework,
        BatchRequest,
        UpdatingPreview,
        CanProceed: () => (requiresAgentAffirmation() && hasAccepted()) || (!requiresAgentAffirmation() && !hasAccepted()), // true
        hasAccepted,
        requiresAgentAffirmation,
        AgentAffirmation,
        ShowAgentAffirmation,
    };
}

export type ReviewTabMilestone = ReturnType<typeof ReviewTabMilestone>;
function ReviewTabMilestone(
    metroMapMilestoneModel : (params : MetroMapMilestoneParams) => MetroMapMilestone,
    ccardModel : (ccard : CoordinationCard) => CoordinationCardViewModel,
    params : MilestoneParams
) {
    const
        { Date } = params,
        metroMapMilestone = metroMapMilestoneModel(params),
        CoordinationCards = Date.CoordinationCards
            .map(ccardModel)
            .sort((ccard1, ccard2) => CoordinationCard.DisplayOrder(ccard1.Model, ccard2.Model));

    return {
        ...metroMapMilestone,
        CoordinationCards
    };
}

export type CoordinationCardViewModel = ReturnType<typeof CoordinationCardViewModel>;
function CoordinationCardViewModel(ccard : CoordinationCard) {
    return { Model: ccard };
}

export type BatchRequestViewModel = ReturnType<typeof BatchRequestViewModel>;
function BatchRequestViewModel(
    ajax : IAjax,
    entrypoints : Entrypoints,
    domain : Domain,
    batchReq : RecurringBatchRequest,
    portcall : PortCall,
    onReviewTab : () => boolean
) {
    const
        StartDate = S(() => portcall.Itinerary().find(pt => pt.IsPortArrival())?.Arrival.Estimated() ?? null),
        // We'll delegate to the server to figure out the overall date range for the requested batch. Of
        // course, we COULD do this date logic client-side, but the server already has responsibility for
        // determining portcall start dates on final submission, and this way we keep all that date logic
        // server-side and minimize the risk that we determine one set of start dates during preview and a
        // DIFFERENT set of start dates on final submission.
        req = S<PreviewBatchDatesRequestData | null>(() => onReviewTab() && StartDate() ? {
            DomainId: domain.Id(),
            StartDate: StartDate()!.toJSON(),
            BatchRequest: JSON.parse(jsonizeSignals(batchReq, [_ => batchReq.Batch]))
        } : null),
        resp = S.value<PreviewBatchDatesResponseData | null>(null),
        client = ajax.Url<PreviewBatchDatesRequestData, PreviewBatchDatesResponseData>(entrypoints.PreviewBatchDates),
        UpdatingPreview = S.value(false),
        reqQueue = new RequestQueue(async () => {
            if (req()) {
                UpdatingPreview(true);
                try {
                    resp(await client.Post(req()!));
                } finally {
                    UpdatingPreview(false);
                }
            }
        }),
        Preview = S(() => {
            if (!resp()) return null;

            const dates = resp()!.PortCallStartDates.map(d => new TZDate(d));

            let minDate : TZDate | null = null;
            for (let date of dates) {
                if (minDate === null || +minDate > +date) minDate = date;
            }

            let maxDate : TZDate | null = null;
            for (let date of dates) {
                if (maxDate === null || +maxDate < +date) maxDate = date;
            }

            return {
                BatchSize: dates.length,
                FirstPortCallArrival: minDate,
                LastPortCallArrival: maxDate
            };
        });

    S(() => req() && reqQueue.enqueueRequest());

    return {
        Model: batchReq,
        StartDate,
        UpdatingPreview,
        Preview
    };
}



// WEBPACK FOOTER //
// ./apps/portcall/addportcall/review.ts